import React, { useEffect, useState } from 'react';
import QuantityTracker from '../QuantityTracker/QuantityTracker';
import ErrorMessage from '../QuantityTracker/ErrorMessage';
import LunaLink from '../../jscomponents/LunaLink/LunaLink';
import './FlyoutCartComponent.scss';

interface PriceType {
  currencyCode: string;
  amount: number;
}

interface LinkType {
  url: string;
  text: string;
}

interface CartComponentProps {
  id: string;
  image?: string;
  name?: string;
  quantity?: number;
  sku: string;
  price?: PriceType;
  duration?: string;
  max?: number;
  link?: LinkType;
  settings?: any;
  isLoading?: boolean;
  onChangeQuantity?: (id: string, sku: string, quantity: number) => void;
  onRemove?: (id: string, sku: string) => void;
}

const defaultSettings = {
  removeBtnLabel: "Remove",
  errorMessageForMaxQuantity: "The quantity selected exceeds the maximum allowed for this transaction. Please contact sales to complete your order.",
}

function CartComponent({
  id,
  image,
  name,
  quantity,
  sku,
  price,
  duration,
  max = 999,
  link,
  settings={ defaultSettings },
  isLoading = false,
  onChangeQuantity,
  onRemove,
}: CartComponentProps) {
  const [count, setCount] = useState<number | string | undefined | null>(quantity)
  const [error, setError] = useState(false)
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setCount(quantity)
  }, [quantity])

  const handleDecrease = () => {
    if (typeof count !== 'number') return

    const newQuanity = count - 1

    setError(newQuanity > max)
    setCount(newQuanity)
    if (onChangeQuantity) { 
      if (timerId) {
        clearTimeout(timerId)
      }         
      const timer = setTimeout(() => {
        onChangeQuantity(id, sku, newQuanity)
        setTimerId(null)
      }, 1000)
      setTimerId(timer)
    }    
  }

  const handleIncrease = () => {
    if (typeof count !== 'number') return

    const newQuanity = count + 1
    setCount(newQuanity)
    if (onChangeQuantity) { 
      if (timerId) {
        clearTimeout(timerId)
      }         
      const timer = setTimeout(() => {
        onChangeQuantity(id, sku, newQuanity)
        setTimerId(null)
      }, 1000)
      setTimerId(timer)
    }   
  }

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.currentTarget.value === '') {
      setCount('')
      return
    }

    const value = parseInt(ev.currentTarget.value)
 
    setError(value > max)
    setCount(value)
  }  

  const handleRemove = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    if (!onRemove) return;
    onRemove(id, sku);
  };

  const handleBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
    if (error) return

    if (typeof count === 'number') {
      if (onChangeQuantity) { 
        onChangeQuantity(id, sku, count)
      }  
    } else {
      setCount(1)
      if (onChangeQuantity) { 
        onChangeQuantity(id, sku, 1)
      }
    }
  };

  const { removeBtnLabel = 'Remove' } = settings
  const hasQuantity = quantity !== undefined && quantity !== null
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: price?.currencyCode || 'USD',
  });
  return (
    <div className="ter-cart-item">
      <figure className="ter-cart-item__figure">
        {image && <img src={image} height="115" width="115" alt="" />}
      </figure>
      <div className="ter-cart-item__details">
        {name && <h3 className="ter-cart-item__product-name">{name}</h3>}
        {sku && <div className="ter-cart-item__sku">SKU: {sku}</div>}
        {
          price &&
          <div className="ter-cart-item__price-wrapper">
            <span className="ter-cart-item__price">{formatter.format(price?.amount)} {price?.currencyCode}</span> {duration && <span className="ter-cart-item__duration">{duration}</span>}
          </div>
        }
        {
          hasQuantity && 
          <div className="ter-cart-item__qty">
            <QuantityTracker 
              labelText=""
              quantityCount={count} 
              min={0}
              max={max}
              error={error}
              disabled={isLoading}
              hasDeleteOption={true}
              handleBlur={handleBlur}
              handleDecreaseQuantity={handleDecrease} 
              handleIncreaseQuantity={handleIncrease} 
              handleChangeQuantity={handleChange} 
            />
            <button className="ter-cart-item__qty__remove" onClick={handleRemove}>
              {removeBtnLabel}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.21 3.71001L14.8 3.30001C14.61 3.11001 14.36 3.01001 14.09 3.01001H9.92C9.65 3.01001 9.4 3.12001 9.21 3.30001L8.8 3.71001C8.61 3.90001 8.36 4.00001 8.09 4.00001H6C5.45 4.00001 5 4.45001 5 5.00001C5 5.55001 5.45 6.00001 6 6.00001H18C18.55 6.00001 19 5.55001 19 5.00001C19 4.45001 18.55 4.00001 18 4.00001H15.91C15.64 4.00001 15.39 3.89001 15.2 3.71001H15.21ZM8 21H16C17.1 21 18 20.1 18 19V7.00001H6V19C6 20.1 6.9 21 8 21ZM13.54 8.78001H14.46V19H13.54V8.78001ZM9.54 8.78001H10.46V19H9.54V8.78001Z" fill="currentColor" />
              </svg>
            </button>
          </div>
        }
        {
          error && <ErrorMessage errorMessage={settings?.errorMessageForMaxQuantity} />
        }
        {
          link &&
          <div className="ter-cart-item__link-wrapper">
            <LunaLink to={link.url} className="ter-cart-item__link">{link.text}</LunaLink>
          </div>
        }
      </div>
    </div>
  );
}

export default CartComponent;
